/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, Slice, PayloadAction } from '@reduxjs/toolkit';

import { OperationStatus } from '../../types';
import ApiError from '../../types/error';
import {
  initialState,
  DevicesInitialState,
  DeviceCategory,
  PostDeviceMappingRequestPayload,
  DeviceType,
  Device,
  DeviceSubType,
  GetDeviceMappingRequestResponse,
  GetDeviceMappingRequestPayload,
  DeviceMappingRequestStatus
} from '../../types/devices';

import { unmarshalError } from '../../utils/common';

const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    getDeviceCategoriesRequest: (
      state: DevicesInitialState,
      action: PayloadAction
    ) => {
      state.getDeviceCategories = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    getDeviceCategoriesSuccess: (
      state: DevicesInitialState,
      action: PayloadAction<DeviceCategory[]>
    ) => {
      state.getDeviceCategories = {
        error: null,
        status: OperationStatus.COMPLETED
      };
      state.deviceCategories = action.payload;
    },
    getDeviceCategoriesFailure: (
      state: DevicesInitialState,
      action: PayloadAction<ApiError>
    ) => {
      state.getDeviceCategories = {
        error: action.payload,
        status: OperationStatus.ERRORED
      };
    },
    getDeviceCategoriesCleanup: (state) => {
      state.deviceCategories = initialState.deviceCategories;
      state.getDeviceCategories = initialState.getDeviceCategories;
    },

    postDeviceMappingRequest: (
      state: DevicesInitialState,
      action: PayloadAction<{
        type: string;
        payload: PostDeviceMappingRequestPayload;
      }>
    ) => {
      if (!state.deviceMappingRequest)
        state.deviceMappingRequest = {} as Record<
          string,
          { error: ApiError; status: OperationStatus }
        >;
      state.deviceMappingRequest[action.payload.type] = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    postDeviceMappingUpdate: (
      state: DevicesInitialState,
      action: PayloadAction<{
        id: string;
        device_type: DeviceType;
        device_sub_type?: DeviceSubType;
      }>
    ) => {
      const { id, device_type, device_sub_type = '' } = action.payload;
      if (!state.devices)
        state.devices = {} as Record<string, Device[] | string>;

      state.devices[
        `${device_type}${device_sub_type ? `_${device_sub_type}` : ''}`
      ] = id;
    },
    postDeviceMappingSuccess: (
      state: DevicesInitialState,
      action: PayloadAction<{
        type: string;
      }>
    ) => {
      state.deviceMappingRequest[action.payload.type] = {
        error: null,
        status: OperationStatus.COMPLETED
      };
    },
    postDeviceMappingFailure: (
      state: DevicesInitialState,
      action: PayloadAction<{ type: string; error: ApiError }>
    ) => {
      state.deviceMappingRequest[action.payload.type] = {
        error: action.payload.error,
        status: OperationStatus.ERRORED
      };
    },
    postDeviceMappingCleanup: (state) => {
      state.postDeviceMappingRequest = initialState.postDeviceMappingRequest;
    },

    getDeviceMappingRequest: (
      state: DevicesInitialState,
      action: PayloadAction<{
        id: string;
        payload: GetDeviceMappingRequestPayload;
      }>
      // eslint-disable-next-line @typescript-eslint/no-empty-function
    ) => {},
    getDeviceMappingSuccess: (
      state: DevicesInitialState,
      action: PayloadAction<{
        data: GetDeviceMappingRequestResponse;
        payload: GetDeviceMappingRequestPayload;
      }>
    ) => {
      const {
        data,
        payload: { device_type, device_sub_type = '', category_type }
      } = action.payload;
      const status = data.status;
      const key = `${device_type}${device_sub_type ? `_${device_sub_type}` : ''}`;
      if (!state.devices) {
        state.devices = {} as Record<string, Device[] | string | ApiError>;
      }

      if (
        status === DeviceMappingRequestStatus.IN_PROGRESS ||
        status === DeviceMappingRequestStatus.QUEUED
      ) {
        return;
      }

      if (status === DeviceMappingRequestStatus.FAILED) {
        const message = 'api-errors.devices.device-mapping.generic-error';
        const err = unmarshalError(message, null);

        if (data.trace_id) {
          err.trace_id = data.trace_id;
        }
        state.devices[key] = err;

        if (state.deviceMappingRequest) {
          state.deviceMappingRequest[category_type] = {
            error: err,
            status: OperationStatus.ERRORED
          };
        }
        return;
      }

      if (status === DeviceMappingRequestStatus.COMPLETED && data.devices) {
        if (state.deviceMappingRequest) {
          state.deviceMappingRequest[category_type] = {
            error: null,
            status: OperationStatus.COMPLETED
          };
        }
        state.devices[key] = data.devices;
      }
    },
    getDeviceMappingFailure: (
      state: DevicesInitialState,
      action: PayloadAction<{
        error: ApiError;
        device_type: DeviceType;
        device_sub_type?: DeviceSubType;
      }>
    ) => {
      state.devices[
        `${action.payload.device_type}${action.payload.device_sub_type ? `_${action.payload.device_sub_type}` : ''}`
      ] = action.payload.error;
    },
    getDeviceMappingCleanup: (state) => {
      state.devices = initialState.devices;
    }
  }
});

export const {
  getDeviceCategoriesRequest,
  getDeviceCategoriesSuccess,
  getDeviceCategoriesFailure,
  getDeviceCategoriesCleanup,

  postDeviceMappingRequest,
  postDeviceMappingUpdate,
  postDeviceMappingSuccess,
  postDeviceMappingFailure,
  postDeviceMappingCleanup,

  getDeviceMappingRequest,
  getDeviceMappingSuccess,
  getDeviceMappingFailure,
  getDeviceMappingCleanup
} = devicesSlice.actions;

const devicesSliceExport: Slice<DevicesInitialState> = devicesSlice;
export default devicesSliceExport;
